import {
    type ReactNode,
    useState
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    ColorPicker,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    DocsTable,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "ColorPicker";

const USAGE_CODE = `const [color, setColor] = useState("#006155");

<ColorPicker
    onChangeEnd={(value) => save(value)}
    onChange={setColor}
    value={color}
/>`;

const FORMATS_CODE = `<ColorPicker
    formats={["hex", "rgb"]}
    initialValue="#006155"
/>

<ColorPicker
    initialValue="#006155"
    initialFormat="oklch"
/>`;

const CONTRAST_CODE = `<ColorPicker
    initialValue="#5180e7"
    contrastSurface="#ffffff"
/>

<ColorPicker
    isContrastShown={false}
    initialValue="#5180e7"
/>`;

const SWATCHES_CODE = `<ColorPicker
    recentColors={["#be3600", "#826200"]}
    presets={[
        { title: "Brand", color: "#006155" },
        { title: "Accent", color: "#5180e7" }
    ]}
/>`;

const ALPHA_CODE = `<ColorPicker
    initialValue="#00615580"
    isAlphaEnabled={true}
/>`;

const DEFAULT_COLOR = "#006155";

const FAILING_COLOR = "#5180e7";

const DARK_SURFACE = "#141414";

const LIGHT_SURFACE = "#ffffff";

const ALPHA_COLOR = "#00615580";

const RECENT_COLORS = [
    "#be3600",
    "#826200",
    "#5180e7"
];

const CUSTOM_PRESETS = [
    {
        title: "#006155",
        color: "#006155"
    },
    {
        title: "#5180e7",
        color: "#5180e7"
    },
    {
        title: "#be3600",
        color: "#be3600"
    },
    {
        title: "#826200",
        color: "#826200"
    }
];

const RGB_FORMATS = [
    "hex",
    "rgb"
] as const;

const stylesheet = StyleSheet.create({
    row: {
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center"
    },
    block: {
        alignSelf: "stretch"
    }
});

const Figure = ({
    children,
    label,
    value
}: {
    children: ReactNode;
    value?: string;
    label: string;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return <View
        style={[
            stylesheet.block,
            {
                gap: spaces.spacingXs
            }
        ]}
    >
        <View
            style={stylesheet.row}
        >
            <Text
                variant="labelMediumSize"
                color="mid"
            >
                {label}
            </Text>
            {
                value ?
                    <Text
                        variant="monoMediumSize"
                        color="high"
                    >
                        {value}
                    </Text>
                :
                    null
            }
        </View>
        {children}
    </View>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("colorPickerPage.formatsTitle"),
        id: "formats"
    },
    {
        title: t("colorPickerPage.contrastTitle"),
        id: "contrast"
    },
    {
        title: t("colorPickerPage.swatchesTitle"),
        id: "swatches"
    },
    {
        title: t("colorPickerPage.alphaTitle"),
        id: "alpha"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const OverviewPreview = () => {
    const {
        t
    } = useDocsI18n();

    const [
        color,
        setColor
    ] = useState<string>(DEFAULT_COLOR);

    return <Preview
        caption={t("colorPickerPage.overviewCaption")}
        isStacked={true}
    >
        <Figure
            label={t("colorPickerPage.valueLabel")}
            value={color}
        >
            <ColorPicker
                onChange={setColor}
                value={color}
            />
        </Figure>
    </Preview>;
};

const ColorPickerPage = () => {
    const {
        t
    } = useDocsI18n();

    return <Article>
        <ComponentHeader
            lead={t("colorPickerPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("colorPickerPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("colorPickerPage.overviewNote")}
            </Paragraph>
            <OverviewPreview />
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("colorPickerPage.formatsTitle")}
            id="formats"
        >
            <Paragraph>
                {t("colorPickerPage.formatsText")}
            </Paragraph>
            <Paragraph>
                {t("colorPickerPage.formatsInput")}
            </Paragraph>
            <Preview
                caption={t("colorPickerPage.formatsCaption")}
                isStacked={true}
            >
                <Figure
                    label={"formats={[\"hex\", \"rgb\"]}"}
                >
                    <ColorPicker
                        initialValue={DEFAULT_COLOR}
                        formats={[
                            ...RGB_FORMATS
                        ]}
                    />
                </Figure>
                <Figure
                    label={"initialFormat=\"oklch\""}
                >
                    <ColorPicker
                        initialValue={DEFAULT_COLOR}
                        initialFormat="oklch"
                    />
                </Figure>
            </Preview>
            <CodeBlock
                code={FORMATS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("colorPickerPage.contrastTitle")}
            id="contrast"
        >
            <Paragraph>
                {t("colorPickerPage.contrastText")}
            </Paragraph>
            <Paragraph>
                {t("colorPickerPage.contrastSnap")}
            </Paragraph>
            <Preview
                caption={t("colorPickerPage.contrastCaption")}
                isStacked={true}
            >
                <Figure
                    label={`contrastSurface="${LIGHT_SURFACE}"`}
                >
                    <ColorPicker
                        contrastSurface={LIGHT_SURFACE}
                        initialValue={FAILING_COLOR}
                    />
                </Figure>
                <Figure
                    label={`contrastSurface="${DARK_SURFACE}"`}
                >
                    <ColorPicker
                        contrastSurface={DARK_SURFACE}
                        initialValue={FAILING_COLOR}
                    />
                </Figure>
            </Preview>
            <CodeBlock
                code={CONTRAST_CODE}
                language="tsx"
            />
            <Callout>
                {t("colorPickerPage.contrastGate")}
            </Callout>
        </Section>
        <Section
            title={t("colorPickerPage.swatchesTitle")}
            id="swatches"
        >
            <Paragraph>
                {t("colorPickerPage.swatchesText")}
            </Paragraph>
            <Preview
                caption={t("colorPickerPage.swatchesCaption")}
                isStacked={true}
            >
                <ColorPicker
                    recentColors={RECENT_COLORS}
                    initialValue={DEFAULT_COLOR}
                    presets={CUSTOM_PRESETS}
                />
            </Preview>
            <CodeBlock
                code={SWATCHES_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("colorPickerPage.alphaTitle")}
            id="alpha"
        >
            <Paragraph>
                {t("colorPickerPage.alphaText")}
            </Paragraph>
            <Paragraph>
                {t("colorPickerPage.alphaEyedropper")}
            </Paragraph>
            <Preview
                caption={t("colorPickerPage.alphaCaption")}
                isStacked={true}
            >
                <Figure
                    label="isAlphaEnabled={true}"
                >
                    <ColorPicker
                        initialValue={ALPHA_COLOR}
                        isAlphaEnabled={true}
                    />
                </Figure>
                <Figure
                    label="isDisabled={true}"
                >
                    <ColorPicker
                        initialValue={DEFAULT_COLOR}
                        isDisabled={true}
                    />
                </Figure>
            </Preview>
            <CodeBlock
                code={ALPHA_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("colorPickerPage.a11yPanel"),
                    t("colorPickerPage.a11yControls"),
                    t("colorPickerPage.a11yPreview"),
                    t("colorPickerPage.a11yContrast"),
                    t("colorPickerPage.a11yFields")
                ]}
            />
            <DocsTable
                rows={[
                    [
                        <InlineCode
                            key="tab"
                        >
                            {t("colorPickerPage.keyTab")}
                        </InlineCode>,
                        t("colorPickerPage.keyTabAction")
                    ],
                    [
                        <InlineCode
                            key="enter"
                        >
                            {t("colorPickerPage.keyEnter")}
                        </InlineCode>,
                        t("colorPickerPage.keyEnterAction")
                    ],
                    [
                        <InlineCode
                            key="escape"
                        >
                            {t("colorPickerPage.keyEscape")}
                        </InlineCode>,
                        t("colorPickerPage.keyEscapeAction")
                    ],
                    [
                        <InlineCode
                            key="arrows"
                        >
                            {t("colorPickerPage.keyArrows")}
                        </InlineCode>,
                        t("colorPickerPage.keyArrowsAction")
                    ]
                ]}
                headers={[
                    t("colorPickerPage.keyColumn"),
                    t("colorPickerPage.actionColumn")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("colorPickerPage.propValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("colorPickerPage.propInitialValue"),
                        defaultValue: "\"#006155\"",
                        name: "initialValue",
                        type: "string"
                    },
                    {
                        description: t("colorPickerPage.propOnChange"),
                        type: "(value: string) => void",
                        name: "onChange"
                    },
                    {
                        description: t("colorPickerPage.propOnChangeEnd"),
                        type: "(value: string) => void",
                        name: "onChangeEnd"
                    },
                    {
                        description: t("colorPickerPage.propIsAlphaEnabled"),
                        name: "isAlphaEnabled",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        defaultValue: "[\"hex\", \"rgb\", \"oklch\"]",
                        description: t("colorPickerPage.propFormats"),
                        type: "Array<\"hex\" | \"rgb\" | \"oklch\">",
                        name: "formats"
                    },
                    {
                        description: t("colorPickerPage.propInitialFormat"),
                        type: "\"hex\" | \"rgb\" | \"oklch\"",
                        defaultValue: "\"hex\"",
                        name: "initialFormat"
                    },
                    {
                        type: "Array<{ title: string; color: string }>",
                        description: t("colorPickerPage.propPresets"),
                        name: "presets"
                    },
                    {
                        type: "Array<{ title: string; color: string } | string>",
                        description: t("colorPickerPage.propRecentColors"),
                        name: "recentColors"
                    },
                    {
                        description: t("colorPickerPage.propIsContrastShown"),
                        name: "isContrastShown",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("colorPickerPage.propContrastSurface"),
                        name: "contrastSurface",
                        type: "string"
                    },
                    {
                        description: t("colorPickerPage.propIsEyeDropperEnabled"),
                        name: "isEyeDropperEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("colorPickerPage.propAreaShape"),
                        type: "\"square\" | \"gamut\"",
                        defaultValue: "\"square\"",
                        name: "areaShape"
                    },
                    {
                        description: t("colorPickerPage.propIsAreaFlush"),
                        defaultValue: "false",
                        name: "isAreaFlush",
                        type: "boolean"
                    },
                    {
                        description: t("colorPickerPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("colorPickerPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("colorPickerPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("colorPickerPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ColorPickerPage;
